/**
 * Lite Squares API Client（公開查詢 by suffix；需 JWT：列出我的、建立）
 */

import { getApiBase } from './apiUtils.js';
import { handleLiteErrorResponse } from './liteApiError.js';

export interface SquareInfo {
  id: string;
  name: string;
  type?: string | null;
  suffix: string;
  isActive: boolean;
}

/**
 * 依後綴查詢 Square 基本資訊（公開，不需登入）
 */
export async function getSquareBySuffix(
  suffix: string
): Promise<SquareInfo | null> {
  const apiBase = getApiBase();
  const { apiFetch } = await import('./apiUtils.js');

  const res = await apiFetch(
    `${apiBase}/api/lite/squares/by-suffix/${encodeURIComponent(suffix)}`,
    { method: 'GET' }
  );

  if (res.status === 404) return null;
  if (!res.ok) throw new Error('無法取得 Square 資訊');

  const json = (await res.json()) as { success: boolean; data?: SquareInfo };
  return json.success && json.data ? json.data : null;
}

/**
 * 列出當前使用者的 Square（需 JWT）
 */
export async function listMySquares(token: string): Promise<SquareInfo[]> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/api/lite/squares`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (res.status === 401 || !res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage: res.status === 401 ? '請先登入' : '無法取得 Square 列表',
    });
  }
  const json = (await res.json()) as { success: boolean; data?: SquareInfo[] };
  return json.success && Array.isArray(json.data) ? json.data : [];
}

/**
 * 建立 Square（需 JWT）
 */
export async function createSquare(
  token: string,
  name: string,
  type?: string | null
): Promise<{ id: string; name: string; type: string | null; suffix: string }> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/api/lite/squares`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: name.trim(),
      ...(type != null && type.trim() ? { type: type.trim() } : {}),
    }),
  });
  if (!res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage: res.status === 401 ? '請先登入' : '建立失敗',
    });
  }
  const json = (await res.json()) as {
    success: boolean;
    data?: { id: string; name: string; type: string | null; suffix: string };
  };
  if (!json.success || !json.data) throw new Error('建立失敗');
  return json.data;
}

/**
 * 依選擇的 Square 換發 JWT（將 lite_square_id 寫入 JWT，與當前 URL 一致）
 * 選擇商家進入時呼叫，取得新 JWT 後儲存再導向 /lite/shop/:suffix
 */
export async function switchSquareForJwt(
  token: string,
  suffix: string
): Promise<string> {
  const apiBase = getApiBase();
  const res = await fetch(
    `${apiBase}/api/lite/auth/switch-square?suffix=${encodeURIComponent(suffix)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  if (!res.ok) {
    await handleLiteErrorResponse(res, {
      defaultMessage:
        res.status === 401
          ? '請先登入'
          : res.status === 403
            ? '您不是該商家的擁有者'
            : res.status === 404
              ? 'Square 不存在或已停用'
              : '換發失敗',
    });
  }
  const json = (await res.json()) as { jwtToken?: string };
  if (!json.jwtToken) throw new Error('換發失敗');
  return json.jwtToken;
}
